import { FieldConfig, ProjectTypeJson } from './ValidationService';
import { unit as unitEnum } from '../enum/project';
import convert, { Unit } from 'convert-units';
import { IGeogridData, IBaseCourseItem } from '../api/IApiResponse';
import { logDebug, logWarn } from '../utils/utils';
import { isConversionNeeded } from '../utils/conversionUtils';
import { normalizeNumberFieldValue } from './WireValueNormalizer';

// Unit mappings from project labels to convert-units identifiers
const UNIT_MAPPINGS: { [key: string]: Unit } = {
    'm²': 'm2',
    'ft²': 'ft2',
    'mm²': 'mm2',
    'in²': 'in2',
    'm³': 'm3',
    'ft³': 'ft3',
    '°': 'deg',
    Psi: 'psi',
    years: 'year',
    kPa: 'kPa',
};

// Conversion precision for numeric results
const CONVERSION_PRECISION = 3;

// Custom conversion factors with source documentation
const CUSTOM_CONVERSIONS: Record<string, Record<string, number>> = {
    // Density conversions
    'kg/m3': {
        'lb/ft3': 0.06242796, // Source: 1 kg/m³ = 0.06242796 lb/ft³
    },
    'lb/ft3': {
        'kg/m3': 16.01846, // Source: 1 lb/ft³ = 16.01846 kg/m³
    },
    // Specific weight conversions
    'kN/m³': {
        'lbf/ft³': 6.36588, // Source: 1 kN/m³ = 6.36588 lbf/ft³
    },
    'lbf/ft³': {
        'kN/m³': 0.1570866, // Source: 1 lbf/ft³ = 0.1570866 kN/m³
    },
    // Speed conversions
    'km/h': {
        'mph': 0.621371, // Source: 1 km/h = 0.621371 mph
    },
    'mph': {
        'km/h': 1.60934, // Source: 1 mph = 1.60934 km/h
    },
};

/**
 * Conversion result interface for better type safety
 */
interface ConversionResult {
    value: number;
    success: boolean;
    method: 'standard' | 'custom' | 'none';
}

/**
 * Unit conversion context for better parameter management
 */
interface ConversionContext {
    fieldName: string;
    sourceUnit: string;
    targetUnit: string;
    sourceValue: number;
    fieldConfig: FieldConfig;
}

/**
 * Gets the correct unit identifier for the 'convert-units' library
 * @param field - Field configuration
 * @param useImperial - True for Imperial unit, false for SI
 * @returns Unit identifier for convert-units, or null if not mappable
 */
const getConvertUnitIdentifier = (field: FieldConfig | undefined, useImperial: boolean): Unit | null => {
    if (!field?.unit || !Array.isArray(field.unit) || field.unit.length !== 2) {
        return null;
    }

    const unitLabel = field.unit[useImperial ? 1 : 0];
    const mappedUnit = UNIT_MAPPINGS[unitLabel] || unitLabel;
    const allUnits = convert().possibilities();

    if (allUnits.includes(mappedUnit as Unit)) {
        return mappedUnit as Unit;
    }

    // 🔧 FIX: Return unit label for CUSTOM_CONVERSIONS lookup even if not in convert-units library.
    // Previous bug: Returning null here caused convertFieldValue to skip conversion entirely,
    // preventing CUSTOM_CONVERSIONS rules (like kN/m³ → lbf/ft³) from being applied.
    // Custom conversions are handled in convertCustomUnit(), which needs the unit string.
    if (CUSTOM_CONVERSIONS[mappedUnit] || Object.values(CUSTOM_CONVERSIONS).some(targets => targets[mappedUnit] !== undefined)) {
        logDebug('UnitConversion', `Unit "${mappedUnit}" not in convert-units but has custom conversion rule`);
        return mappedUnit as Unit;
    }

    logWarn('UnitConversion', `Unit "${mappedUnit}" (from "${unitLabel}") not recognized by convert-units for field "${field.name}"`);
    return null;
};

/**
 * Performs custom unit conversions for units not supported by convert-units
 * @param value - Value to convert
 * @param fromUnit - Source unit
 * @param toUnit - Target unit
 * @returns Converted value or original value if no conversion rule exists
 */
const convertCustomUnit = (value: number, fromUnit: string, toUnit: string): number => {
    logDebug('UnitConversion', `Attempting custom conversion: ${value} ${fromUnit} -> ${toUnit}`);

    // Check if conversion rule exists
    const conversionFactor = CUSTOM_CONVERSIONS[fromUnit]?.[toUnit];
    if (conversionFactor !== undefined) {
        const convertedValue = value * conversionFactor;
        logDebug('UnitConversion', `Custom conversion applied: ${value} ${fromUnit} = ${convertedValue} ${toUnit} (factor: ${conversionFactor})`);
        return convertedValue;
    }

    logWarn('UnitConversion', `No custom conversion rule for ${fromUnit} → ${toUnit}`);
    return value;
};

/**
 * Attempts standard conversion using convert-units library
 * @param context - Conversion context
 * @returns Conversion result
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const attemptStandardConversion = (context: ConversionContext): ConversionResult => {
    const { sourceUnit, targetUnit, sourceValue, fieldConfig, fieldName } = context;

    try {
        const sourceLibUnit = getConvertUnitIdentifier(fieldConfig, sourceUnit === fieldConfig.unit?.[1]);
        const targetLibUnit = getConvertUnitIdentifier(fieldConfig, targetUnit === fieldConfig.unit?.[1]);

        if (sourceLibUnit && targetLibUnit && sourceLibUnit !== targetLibUnit) {
            const convertedValue = convert(sourceValue).from(sourceLibUnit).to(targetLibUnit);
            return { value: convertedValue, success: true, method: 'standard' };
        }
    } catch (error) {
        logWarn('UnitConversion', `Standard conversion failed for ${fieldName}:`, error);
    }

    return { value: sourceValue, success: false, method: 'standard' };
};

/**
 * Attempts custom conversion for non-standard units
 * @param context - Conversion context
 * @returns Conversion result
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const attemptCustomConversion = (context: ConversionContext): ConversionResult => {
    const { sourceUnit, targetUnit, sourceValue } = context;

    const customValue = convertCustomUnit(sourceValue, sourceUnit, targetUnit);
    const success = customValue !== sourceValue;

    return {
        value: customValue,
        success,
        method: 'custom',
    };
};

/**
 * Converts a single field value from source to target unit system
 * @param fieldConfig - Field configuration
 * @param currentValue - Current field value
 * @param sourceUnit - Source unit system
 * @param targetUnit - Target unit system
 * @param fieldPath - Field path for logging (e.g., "group.field")
 * @returns Converted value or null if conversion failed/not needed
 */

/**
 * Converts API data from a source unit system to a target unit system.
 * @param projectInfoData - Raw project data (object containing data for a specific method, e.g., { bcr: { ... } } or { static: { ... } })
 * @param method - Calculation method key (e.g., "bcr", "static") corresponding to a key in projectInfoData
 * @param fields - Field configurations for the method
 * @param sourceUnitSystem - The source unit system of the projectInfoData
 * @param targetUnitSystem - The target unit system for the conversion
 * @returns Converted data object (or original if no conversion needed/possible), structured like projectInfoData
 */
export const convertApiData = (
    projectInfoData: Record<string, any> | undefined,
    method: string,
    fields: FieldConfig[],
    sourceUnitSystem: unitEnum,
    targetUnitSystem: unitEnum
): Record<string, any> | undefined => {
    // If units are the same or data for the method doesn't exist, return original
    if (sourceUnitSystem === targetUnitSystem || !projectInfoData?.[method]) {
        logDebug('UnitConversion', `Skipping API data conversion. Source: ${sourceUnitSystem}, Target: ${targetUnitSystem}, Data for method '${method}' exists: ${!!projectInfoData?.[method]}`);
        return projectInfoData;
    }

    // Special debug logging for Container Yard conversions
    if (method === 'bcr' || method === 'static') {
        logDebug('CONTAINER_YARD_CONVERSION', `Converting Container Yard ${method} data from ${sourceUnitSystem} to ${targetUnitSystem}`, {
            method,
            sourceUnitSystem,
            targetUnitSystem,
            dataKeys: Object.keys(projectInfoData[method] || {}),
            fieldsCount: fields.length,
            fieldNames: fields.map(f => f.name).slice(0, 10),
        });
    }

    logDebug('UnitConversion', `Converting API data for method '${method}' from ${sourceUnitSystem} to ${targetUnitSystem}`);
    // Deep copy the specific method's data to avoid mutating the original object
    const methodDataToConvert = JSON.parse(JSON.stringify(projectInfoData[method]));

    Object.keys(methodDataToConvert).forEach(groupName => {
        const groupData = methodDataToConvert[groupName];
        if (!groupData || typeof groupData !== 'object') return;

        Object.keys(groupData).forEach(fieldName => {
            const fieldConfig = fields.find(f => f.name === fieldName);
            let currentValue = groupData[fieldName];

            // Only number fields parse, and only whole valid numeric strings (run-text-fields C3)
            if (fieldConfig?.type === 'number') {
                const parsedValue = normalizeNumberFieldValue(currentValue);
                if (parsedValue !== currentValue) {
                    currentValue = parsedValue;
                    groupData[fieldName] = currentValue;
                }
            }

            if (typeof currentValue === 'number' && fieldConfig) {
                // Pass the correct source and target unit systems to convertFieldValue
                const convertedValue = convertFieldValue(fieldConfig, currentValue, sourceUnitSystem, targetUnitSystem, `${groupName}.${fieldName}`);

                if (convertedValue !== null) {
                    groupData[fieldName] = convertedValue;
                }
            }
        });
    });

    // Return a new object with the original projectInfoData structure,
    // but with the specific method's data replaced by the converted version.
    return {
        ...projectInfoData,
        [method]: methodDataToConvert,
    };
};

/**
 * Converts FLAT structure data from a source unit system to a target unit system.
 * Unlike convertApiData which expects nested data under a method key,
 * this function works directly with flat data where fields are at the root level.
 *
 * @param data - Flat data object with groups directly at root (e.g., { roadInfo: {...}, trafficLoad: {...} })
 * @param fields - Field configurations for conversion
 * @param sourceUnitSystem - The source unit system of the data
 * @param targetUnitSystem - The target unit system for the conversion
 * @returns Converted data object (or original if no conversion needed)
 */
export const convertFlatData = (data: Record<string, any> | undefined, fields: FieldConfig[], sourceUnitSystem: unitEnum, targetUnitSystem: unitEnum): Record<string, any> | undefined => {
    // If units are the same or no data, return original
    if (sourceUnitSystem === targetUnitSystem || !data) {
        console.log(`[UnitConversion] Skipping FLAT data conversion. Source: ${sourceUnitSystem}, Target: ${targetUnitSystem}`);
        return data;
    }

    console.log(`[UnitConversion] Converting FLAT data from ${sourceUnitSystem} to ${targetUnitSystem}`);

    // Deep copy to avoid mutating the original object
    const dataToConvert = JSON.parse(JSON.stringify(data));

    Object.keys(dataToConvert).forEach(groupName => {
        const groupData = dataToConvert[groupName];
        if (!groupData || typeof groupData !== 'object') return;

        Object.keys(groupData).forEach(fieldName => {
            const fieldConfig = fields.find(f => f.name === fieldName);
            let currentValue = groupData[fieldName];

            // Only number fields parse, and only whole valid numeric strings (run-text-fields C3)
            if (fieldConfig?.type === 'number') {
                const parsedValue = normalizeNumberFieldValue(currentValue);
                if (parsedValue !== currentValue) {
                    currentValue = parsedValue;
                    groupData[fieldName] = currentValue;
                }
            }

            if (typeof currentValue === 'number' && fieldConfig) {
                const convertedValue = convertFieldValue(fieldConfig, currentValue, sourceUnitSystem, targetUnitSystem, `${groupName}.${fieldName}`);

                if (convertedValue !== null) {
                    groupData[fieldName] = convertedValue;
                }
            }
        });
    });

    return dataToConvert;
};

/**
 * Converts GeoGrid data between unit systems
 * @param geoGridData - GeoGrid data to convert
 * @param configFields - Field configurations from GeoGrid config
 * @param targetUnit - Target unit system
 * @param projectUnit - Current project unit system (for conversion state detection)
 * @param conversionApplied - Whether conversion has already been applied to the data
 * @param originalUnit - Original unit system before any conversions
 * @returns Converted GeoGrid data
 */
export const convertGeoGridData = (
    geoGridData: Partial<IGeogridData> | undefined | null,
    configFields: ProjectTypeJson['fields'],
    targetUnit: unitEnum,
    projectUnit?: unitEnum,
    conversionApplied?: boolean,
    originalUnit?: unitEnum
): Partial<IGeogridData> | undefined | null => {
    if (!geoGridData) {
        logDebug('UnitConversion', 'Skipping GeoGrid conversion - no data provided');
        return geoGridData;
    }

    // Use conversion state management to determine if conversion is needed
    const currentUnit = projectUnit || unitEnum.SI; // Default to SI if not specified for backward compatibility
    const decision = isConversionNeeded(targetUnit, currentUnit, conversionApplied || false, originalUnit);

    logDebug('UnitConversion', `GeoGrid conversion decision: ${decision.reason}`, {
        targetUnit,
        currentUnit,
        conversionApplied,
        originalUnit,
        conversionNeeded: decision.conversionNeeded,
        confidence: decision.confidence,
    });

    if (!decision.conversionNeeded) {
        logDebug('UnitConversion', 'Skipping GeoGrid conversion based on conversion state');
        return geoGridData;
    }

    const sourceUnit = decision.sourceUnit;
    logDebug('UnitConversion', `Converting GeoGrid data from ${sourceUnit} to ${targetUnit}`);
    const convertedData: Partial<IGeogridData> = JSON.parse(JSON.stringify(geoGridData));

    Object.entries(configFields).forEach(([groupKey, groupFieldConfigs]) => {
        if (groupKey === 'geogridItem') return;

        const groupData = convertedData[groupKey as keyof IGeogridData];
        if (!groupData || typeof groupData !== 'object' || Array.isArray(groupData)) return;

        convertGroupData(groupData as Record<string, unknown>, groupFieldConfigs, configFields, sourceUnit, targetUnit, groupKey);
    });

    return convertedData;
};

/**
 * Converts data for a specific group in GeoGrid
 * @param groupData - Group data object
 * @param groupFieldConfigs - Field configurations for the group
 * @param configFields - All field configurations
 * @param sourceUnit - Source unit system
 * @param targetUnit - Target unit system
 * @param groupKey - Group identifier for logging
 */
const convertGroupData = (
    groupData: Record<string, unknown>,
    groupFieldConfigs: FieldConfig[],
    configFields: ProjectTypeJson['fields'],
    sourceUnit: unitEnum,
    targetUnit: unitEnum,
    groupKey: string
): void => {
    groupFieldConfigs.forEach(fieldConfig => {
        const fieldName = fieldConfig.name;

        if (fieldName === 'geogrids' && Array.isArray(groupData[fieldName])) {
            convertGeogridItems(groupData[fieldName] as IBaseCourseItem[], configFields, sourceUnit, targetUnit, groupKey);
        } else {
            const currentValue = groupData[fieldName];
            if (typeof currentValue === 'number' && fieldConfig) {
                const convertedValue = convertFieldValue(fieldConfig, currentValue, sourceUnit, targetUnit, `${groupKey}.${fieldName}`);

                if (convertedValue !== null) {
                    groupData[fieldName] = convertedValue;
                }
            }
        }
    });
};

/**
 * Converts geogrid items array
 * @param geogridItems - Array of geogrid items
 * @param configFields - All field configurations
 * @param sourceUnit - Source unit system
 * @param targetUnit - Target unit system
 * @param groupKey - Group identifier for logging
 */
const convertGeogridItems = (geogridItems: IBaseCourseItem[], configFields: ProjectTypeJson['fields'], sourceUnit: unitEnum, targetUnit: unitEnum, groupKey: string): void => {
    const geogridItemConfig = configFields['geogridItem'];
    if (!geogridItemConfig) return;

    geogridItems.forEach((item, index) => {
        geogridItemConfig.forEach(itemFieldConfig => {
            const itemFieldName = itemFieldConfig.name;
            const currentItemValue = (item as any)[itemFieldName];

            if (typeof currentItemValue === 'number' && itemFieldConfig) {
                const convertedValue = convertFieldValue(itemFieldConfig, currentItemValue, sourceUnit, targetUnit, `${groupKey}[${index}].${itemFieldName}`);

                if (convertedValue !== null) {
                    (item as any)[itemFieldName] = convertedValue;
                }
            }
        });
    });
};

/**
 * Converts a single field's value.
 * @param fieldConfig - Configuration for the field.
 * @param value - Current value of the field.
 * @param sourceUnitSystem - The unit system of the `value`.
 * @param targetUnitSystem - The unit system to convert `value` to.
 * @param fieldPath - Path of the field for logging.
 * @returns Converted value, or null if no conversion needed/possible or error.
 */

const convertFieldValue = (fieldConfig: FieldConfig, value: unknown, sourceUnitSystem: unitEnum, targetUnitSystem: unitEnum, fieldPath: string): number | null => {
    // Input validation
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        logWarn('UnitConversion', `Invalid numeric value for field "${fieldPath}": ${value}`);
        return null;
    }

    if (!fieldConfig?.isConvertible || sourceUnitSystem === targetUnitSystem) {
        return null; // No conversion needed or not a convertible number
    }

    const sourceUnitId = getConvertUnitIdentifier(fieldConfig, sourceUnitSystem === unitEnum.Imperial);
    const targetUnitId = getConvertUnitIdentifier(fieldConfig, targetUnitSystem === unitEnum.Imperial);

    if (!sourceUnitId || !targetUnitId) {
        logWarn('UnitConversion', `Cannot convert field "${fieldPath}": Source or target unit identifier is null. Source: ${sourceUnitId}, Target: ${targetUnitId}`);
        return null; // Cannot determine units for conversion
    }

    if (sourceUnitId === targetUnitId) {
        return value; // Units are effectively the same after mapping
    }

    // Attempt custom conversion first — check rule existence, not value comparison
    const hasCustomRule = CUSTOM_CONVERSIONS[sourceUnitId]?.[targetUnitId] !== undefined;
    if (hasCustomRule) {
        const customConvertedValue = convertCustomUnit(value, sourceUnitId, targetUnitId);
        logDebug('UnitConversion', `Field "${fieldPath}" (${value} ${sourceUnitId}) custom converted to ${customConvertedValue} ${targetUnitId}`);
        return roundTo3Decimals(customConvertedValue);
    }

    // Fallback to convert-units library if no custom rule applied
    try {
        const converted = convert(value)
            .from(sourceUnitId as Unit)
            .to(targetUnitId as Unit);
        logDebug('UnitConversion', `Field "${fieldPath}" (${value} ${sourceUnitId}) converted to ${converted} ${targetUnitId}`);
        return roundTo3Decimals(converted);
    } catch (e) {
        logWarn('UnitConversion', `Error converting field "${fieldPath}" from ${sourceUnitId} to ${targetUnitId}:`, e);
        return null; // Return null on error to indicate conversion failure
    }
};

/**
 * Rounds a number to the specified decimal places
 * @param value - The number to round
 * @returns Rounded number to CONVERSION_PRECISION decimal places
 */
const roundTo3Decimals = (value: number): number => {
    const factor = Math.pow(10, CONVERSION_PRECISION);
    return Math.round((value + Number.EPSILON) * factor) / factor;
};

// Ensure convertFieldValue is listed ONCE in your final export block
export { getConvertUnitIdentifier, convertCustomUnit, convertFieldValue };
